import { EmptyState } from '@monorepo/shared/componentsV2/EmptyState';
import {
  Page,
  Title,
} from '@monorepo/shared/componentsV2/fullPageWizard/styled/fullPageWizard';
import { FullScreenLoadingPage } from '@monorepo/shared/componentsV2/Loading';
import { useHasOrganizationUpdatePermissions } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { useDisableSubnav } from '@monorepo/shared/hooks/subnav/useDisableSubnav';
import { FormState } from 'final-form';
import { Category } from 'mapistry-shared';
import { useFeatureFlags } from 'mapistry-shared/api';
import React, { useCallback, useMemo } from 'react';
import { Form, FormSpy } from 'react-final-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useFormTemplateContext } from '../EditFormTemplatePage/FormTemplateContext';
import { FormTemplateUsage } from '../EditFormTemplatePage/FormTemplateUsage';
import {
  ColumnWrapper,
  MainContent,
  Sidebar,
  StyledMain,
} from '../FormTemplateEditor/styled';
import { VersionWarning } from '../FormTemplateEditor/VersionWarning';
import { useRandomFormTemplateSlug } from '../GenerateFormTemplatePage/hooks/useRandomFormTemplateSlug';
import { WizardStep } from '../shared/consts';
import { FormTemplateHeader } from '../shared/FormTemplateHeader';
import { FormTemplateDetailsFields } from './FormTemplateDetailsFields';
import { useSubmitFormTemplateDetails } from './useSubmitFormTemplateDetails';

const StyledFormDetailInputs = styled.div`
  width: 30rem;
`;

type FormValues = {
  name: string;
  slug: string;
  category: Category | undefined | null;
};

function useGetInitialValues() {
  const {
    formTemplate: { name, slug, category },
  } = useFormTemplateContext();

  const randomFormTemplateSlug = useRandomFormTemplateSlug();
  const initialValues = useMemo<FormValues>(
    () => ({
      name,
      slug: slug || randomFormTemplateSlug,
      category,
    }),
    [name, slug, category, randomFormTemplateSlug],
  );

  return initialValues;
}

export function EditFormTemplateDetailsPage({
  formWizardBasePath,
  isEditMode,
}: {
  formWizardBasePath: string;
  isEditMode: boolean;
}) {
  useDisableSubnav();
  const { organizationId } = useParams();
  const { isAssigningFormCalendarsEnabled } = useFeatureFlags();
  const isUsingOrgSpecificEditor = !!organizationId;
  const hasOrgUpdatePermissions =
    useHasOrganizationUpdatePermissions(organizationId);

  const {
    formTemplate,
    isDirty,
    isLoading,
    isError,
    formTemplateHelpers: { updateBasicInfo },
  } = useFormTemplateContext();

  const onValuesChange = useCallback(
    (props: FormState<FormValues>) => {
      updateBasicInfo(
        props.values.name,
        props.values.slug,
        props.values.category || null,
      );
    },
    [updateBasicInfo],
  );

  const initialValues = useGetInitialValues();
  const onSubmit = useSubmitFormTemplateDetails({
    formTemplate,
    organizationId,
    isEditMode,
    formWizardBasePath,
  });

  // org admins can see the assign tab, while site admins (for now, April 2026) cannot
  const canSeeAssignTab =
    isAssigningFormCalendarsEnabled && hasOrgUpdatePermissions;
  const showUsage =
    isEditMode &&
    isUsingOrgSpecificEditor &&
    // don't show the usage here when the user can see the Assign tab
    !canSeeAssignTab;

  if (isLoading) return <FullScreenLoadingPage />;

  return isError ? (
    <EmptyState primaryText="There was a problem loading the form." />
  ) : (
    <Form<FormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      subscription={{ pristine: true, submitting: true, valid: true }}
    >
      {({ submitting, handleSubmit, valid }) => (
        <>
          <FormSpy subscription={{ values: true }} onChange={onValuesChange} />
          <Page>
            <FormTemplateHeader
              formWizardBasePath={formWizardBasePath}
              organizationId={organizationId}
              canSubmit={isDirty}
              isEditMode={isEditMode}
              wizardStep={WizardStep.DETAILS}
              isSubmitting={submitting}
              onSubmit={handleSubmit}
              isBasicInfoInvalid={!valid}
            />
            <StyledMain>
              <ColumnWrapper>
                {showUsage && (
                  <Sidebar>
                    <FormTemplateUsage
                      organizationId={organizationId}
                      formTemplateSlug={formTemplate?.slug}
                    />
                  </Sidebar>
                )}
                <MainContent>
                  {isEditMode && isDirty && <VersionWarning />}

                  <Title>Basic Info</Title>
                  <StyledFormDetailInputs>
                    <FormTemplateDetailsFields isEditMode={isEditMode} />
                  </StyledFormDetailInputs>
                </MainContent>
              </ColumnWrapper>
            </StyledMain>
          </Page>
        </>
      )}
    </Form>
  );
}
